// "All filters" for the Recipes library (wireframe 17): a bottom sheet on a
// phone, a panel beside the list on a wide screen. Each group is a row of
// chips; tapping the chosen one again clears it.
import type { ReactNode } from "react";
import { COOKING_METHODS, DIET_TAGS, DIET_TAG_LABELS, MEAL_TYPES, RECIPE_TYPES, RECIPE_TYPE_LABELS, SEASONS, SPACE } from "../../lib/constants";
import type { LibraryFilters as Filters, TimeLimit, Whose } from "../../lib/recipeLibrary";
import { smallLabel } from "../recipeStyles";
import { chip } from "../plan/planStyles";

const WHOSE: [Whose | "all", string][] = [
  ["all", "All"],
  ["mine", "Mine"],
  ["household", "Household"],
  ["shared", "Shared with me"],
  ["public", "Public"],
];
const TIMES: [TimeLimit, string][] = [
  ["any", "Any"],
  ["30", "Under 30 min"],
  ["60", "Under 1 hour"],
];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function LibraryFilters({ filters, onChange, cuisines }: { filters: Filters; onChange: (f: Filters) => void; cuisines: string[] }) {
  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });
  // A text filter: the chosen chip again clears it.
  const pick = (key: "dishType" | "meal" | "diet" | "method" | "cuisine", value: string) => set({ [key]: filters[key] === value ? "" : value });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xxl }}>
      <Group title="Favorites">
        <Chip on={filters.favorites} onClick={() => set({ favorites: !filters.favorites })}>
          Favorites only
        </Chip>
      </Group>
      <Group title="Whose recipes">
        {WHOSE.map(([value, label]) => (
          <Chip key={value} on={filters.whose === value} onClick={() => set({ whose: value })}>
            {label}
          </Chip>
        ))}
      </Group>
      <Group title="Dish type">
        {RECIPE_TYPES.map((t) => (
          <Chip key={t} on={filters.dishType === t} onClick={() => pick("dishType", t)}>
            {(RECIPE_TYPE_LABELS as Record<string, string>)[t]}
          </Chip>
        ))}
      </Group>
      <Group title="Meal">
        {MEAL_TYPES.map((m) => (
          <Chip key={m} on={filters.meal === m} onClick={() => pick("meal", m)}>
            {cap(m)}
          </Chip>
        ))}
      </Group>
      <Group title="Season">
        {SEASONS.map((s) => (
          <Chip key={s} on={!filters.inSeason && filters.season === s} onClick={() => set({ inSeason: false, season: filters.season === s && !filters.inSeason ? "" : s })}>
            {s}
          </Chip>
        ))}
      </Group>
      <Group title="Diet">
        {DIET_TAGS.map((d) => (
          <Chip key={d} on={filters.diet === d} onClick={() => pick("diet", d)}>
            {(DIET_TAG_LABELS as Record<string, string>)[d]}
          </Chip>
        ))}
      </Group>
      <Group title="Cooking method">
        {COOKING_METHODS.map((m) => (
          <Chip key={m} on={filters.method === m} onClick={() => pick("method", m)}>
            {m}
          </Chip>
        ))}
      </Group>
      {cuisines.length > 0 && (
        <Group title="Cuisine">
          {cuisines.map((c) => (
            <Chip key={c} on={filters.cuisine === c} onClick={() => pick("cuisine", c)}>
              {c}
            </Chip>
          ))}
        </Group>
      )}
      <Group title="Total time">
        {TIMES.map(([value, label]) => (
          <Chip key={value} on={filters.time === value} onClick={() => set({ time: value })}>
            {label}
          </Chip>
        ))}
      </Group>
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
      <legend style={{ ...smallLabel, padding: 0, marginBottom: SPACE.sm }}>{title}</legend>
      <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm }}>{children}</div>
    </fieldset>
  );
}

export function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} style={chip(on ? "accent" : "plain")}>
      {children}
    </button>
  );
}
